import React, { useRef, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Pause, Volume2, VolumeX, Maximize, Minimize } from 'react-feather';

export function VideoPlayer({ src, poster, className = '', rounded = 'rounded-xl' }) {
  const videoRef = useRef(null);
  const hideTimeoutRef = useRef(null);

  // State
  const [isPlaying, setIsPlaying] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);

  // Show controls + reset auto-hide timer
  const showControls = () => {
    setControlsVisible(true);

    // Clear old timer
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);

    // Auto-hide after 3 seconds
    hideTimeoutRef.current = setTimeout(() => {
      setControlsVisible(false);
    }, 3000);
  };

  const togglePlay = () => {
    if (!videoRef.current) return;
    isPlaying ? videoRef.current.pause() : videoRef.current.play();
    showControls();
  };

  const toggleMute = () => {
    setMuted(!muted);
    videoRef.current.muted = !muted;
    showControls();
  };

  const handleTimeUpdate = () => {
    const v = videoRef.current;
    setProgress((v.currentTime / v.duration) * 100);
  };

  const handleProgressClick = (e) => {
    const rect = e.target.getBoundingClientRect();
    const pos = (e.clientX - rect.left) / rect.width;
    videoRef.current.currentTime = pos * duration;
    showControls();
  };

  const handleLoadedMetadata = () => {
    setDuration(videoRef.current.duration);
  };

  const toggleFullscreen = () => {
    const wrapper = videoRef.current.parentElement;

    if (!document.fullscreenElement) {
      wrapper.requestFullscreen();
      setFullscreen(true);
    } else {
      document.exitFullscreen();
      setFullscreen(false);
    }
    showControls();
  };

  const formatTime = (t) => {
    if (!t || isNaN(t)) return '0:00';
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60)
      .toString()
      .padStart(2, '0');
    return `${m}:${s}`;
  };

  // Hide controls after inactivity
  useEffect(() => {
    showControls(); // initial show
  }, []);

  // Mouse movement should reveal controls
  const handleMouseMove = () => showControls();

  return (
    <div
      className={`relative bg-black overflow-hidden ${rounded} ${className}`}
      onMouseMove={handleMouseMove}
      onClick={() => setControlsVisible(!controlsVisible)} // mobile tap toggle
    >
      {/* VIDEO */}
      <video
        ref={videoRef}
        src={src}
        poster={poster}
        className="w-full h-full object-cover"
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onClick={togglePlay}
        onPlay={() => {
          setIsPlaying(true);
          showControls();
        }}
        onPause={() => {
          setIsPlaying(false);
          showControls();
        }}
      />

      {/* CONTROLS */}
      <AnimatePresence>
        {controlsVisible && (
          <motion.div
            key="controls"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="absolute inset-0 flex flex-col justify-end bg-gradient-to-t from-black/60 via-black/20 to-transparent p-4"
          >
            {/* Timeline */}
            <div
              className="w-full h-1.5 bg-white/30 rounded cursor-pointer mb-4"
              onClick={handleProgressClick}
            >
              <div className="h-full bg-[#AC3F1E] rounded" style={{ width: `${progress}%` }} />
            </div>

            {/* CONTROL BAR */}
            <div className="flex items-center justify-between text-white text-sm">
              {/* Left */}
              <div className="flex items-center gap-4">
                <button onClick={togglePlay}>
                  {isPlaying ? <Pause size={20} /> : <Play size={20} />}
                </button>

                <span>
                  {formatTime(videoRef.current?.currentTime)} / {formatTime(duration)}
                </span>
              </div>

              {/* Right */}
              <div className="flex items-center gap-4">
                <button onClick={toggleMute}>
                  {muted || volume === 0 ? <VolumeX size={20} /> : <Volume2 size={20} />}
                </button>

                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={muted ? 0 : volume}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    setVolume(v);
                    videoRef.current.volume = v;
                    if (v === 0) setMuted(true);
                    showControls();
                  }}
                  className="w-24 accent-[#AC3F1E]"
                />

                <button onClick={toggleFullscreen}>
                  {fullscreen ? <Minimize size={20} /> : <Maximize size={20} />}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
